import { Phone, CalendarClock, Flame, Clock, MapPin, Home, User, TrendingUp } from "lucide-react";
import StarScore from "@/components/crm/StarScore";
import { ScoreChip } from "@/components/crm/ScoreBadge";
import {
  formatDate,
  getInitials,
  daysSince,
  isOverdue,
  isDueToday,
  bucketOf,
  BUCKET_META,
  typeAppartLabel,
  programmeProgress,
  formatFCFA,
} from "@/lib/crmHelpers";

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const deadlineLabel = (iso) => {
  const d = daysUntil(iso);
  if (d === null) return null;
  if (d < 0) return `J${d} · en retard`;
  if (d === 0) return "Aujourd'hui";
  if (d === 1) return "Demain";
  return `J+${d}`;
};

// Banner config for the most-impactful signals to surface on a kanban card.
const SIGNAL_BANNER = {
  ready_to_close:    { icon: "🎯", label: "Prêt à closer",     tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  going_cold:        { icon: "❄️", label: "Refroidissement",   tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  never_contacted:   { icon: "⚠️", label: "Jamais contacté",   tone: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30" },
  overdue_action:    { icon: "⏰", label: "Action en retard",  tone: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30" },
  competitor_mention:{ icon: "🥊", label: "Concurrent évoqué", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  premium_qualified: { icon: "💎", label: "Premium qualifié",  tone: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30" },
};

// Top-priority signal among many — display only one banner per card.
const TOP_SIGNAL_ORDER = [
  "ready_to_close",
  "overdue_action",
  "never_contacted",
  "going_cold",
  "competitor_mention",
  "premium_qualified",
];

const pickTopSignal = (signals = []) => {
  for (const s of TOP_SIGNAL_ORDER) if (signals.includes(s)) return s;
  return null;
};

export default function LeadCard({
  lead,
  insights,
  dragging,
  onDragStart,
  onDragEnd,
  onClick,
}) {
  const hot = (lead.score || 0) >= 4 || lead.priorite === "haute";
  const stale =
    daysSince(lead.derniere_action) > 2 &&
    lead.statut !== "gagne" &&
    lead.statut !== "perdu";
  const dueSoon =
    isOverdue(lead.prochaine_action) || isDueToday(lead.prochaine_action);
  const bucket = bucketOf(lead);
  const bMeta = BUCKET_META[bucket];
  const progress = programmeProgress(lead.programme);

  const qualityScore = insights?.quality_score;
  const probability = insights?.probability ?? lead.probabilite;
  const topSignal = pickTopSignal(insights?.signals);

  // v2.6.0 — Firefox requires dataTransfer.setData() in dragstart to actually
  // initiate the drag (Chromium tolerates its absence). We also set
  // effectAllowed so the cursor shows a "move" icon, not "copy".
  const onDragStartWrapped = (e) => {
    if (e?.dataTransfer) {
      try {
        e.dataTransfer.setData("text/plain", lead.id);
        e.dataTransfer.effectAllowed = "move";
      } catch {
        /* old IE / restricted contexts — fall through */
      }
    }
    onDragStart?.(e);
  };

  return (
    <div
      draggable
      onDragStart={onDragStartWrapped}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`lead-card-${lead.id}`}
      className={`kanban-card group relative overflow-hidden rounded-lg border bg-card p-3 transition-all duration-150 hover:-translate-y-[1px] hover:border-foreground/20 hover:shadow-sm ${
        dragging ? "dragging" : ""
      } ${hot ? "border-red-200 dark:border-red-900/40" : "border-border/60"}`}
    >
      {hot && (
        <span
          className="absolute left-0 top-0 h-full w-[2px] bg-red-500"
          aria-hidden
        />
      )}

      {topSignal && (
        <div
          className={`mb-2 -mx-3 -mt-3 flex items-center gap-1.5 border-b px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${SIGNAL_BANNER[topSignal].tone}`}
          data-testid={`signal-${topSignal}`}
        >
          <span>{SIGNAL_BANNER[topSignal].icon}</span>
          <span>{SIGNAL_BANNER[topSignal].label}</span>
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
            {getInitials(lead.nom)}
          </div>
          <div className="min-w-0">
            <div className="font-heading truncate text-sm font-semibold">
              {lead.nom}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="truncate">{lead.telephone}</span>
            </div>
          </div>
        </div>
        <span
          className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${bMeta.color}`}
          title={bMeta.full}
        >
          {bMeta.label}
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <StarScore value={lead.score || 0} readOnly size="sm" testId={`lead-card-stars-${lead.id}`} />
          {qualityScore != null && <ScoreChip score={qualityScore} size="sm" />}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {lead.type_appart && lead.type_appart !== "autre" && (
            <span className="inline-flex items-center gap-0.5">
              <Home className="h-3 w-3" />
              {typeAppartLabel(lead.type_appart).toUpperCase()}
            </span>
          )}
          {lead.localisation && (
            <span className="inline-flex items-center gap-0.5">
              <MapPin className="h-3 w-3" />
              {lead.localisation === "dakar" ? "DK" : lead.localisation === "diaspora" ? "DIASP" : "—"}
            </span>
          )}
        </div>
      </div>

      {(lead.budget || lead.montant_prevu || lead.commercial) && (
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
          <div className="flex items-center gap-1.5 truncate">
            {lead.montant_prevu > 0 ? (
              <>
                <span className="font-semibold text-[hsl(var(--accent-gold))]">
                  {formatFCFA(lead.montant_prevu)}
                </span>
                {probability > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded-sm bg-[hsl(var(--accent-navy))]/10 px-1 text-[10px] font-bold text-[hsl(var(--accent-navy))]"
                    title={`Probabilité de closing : ${probability}%`}
                  >
                    <TrendingUp className="h-2.5 w-2.5" />
                    {probability}%
                  </span>
                )}
              </>
            ) : lead.budget ? (
              <span className="font-semibold text-[hsl(var(--accent-gold))]">
                {lead.budget}
              </span>
            ) : null}
          </div>
          {lead.commercial && (
            <span className="inline-flex items-center gap-0.5 truncate text-muted-foreground">
              <User className="h-2.5 w-2.5" />
              <span className="truncate max-w-[100px]">{lead.commercial}</span>
            </span>
          )}
        </div>
      )}

      {lead.notes && (
        <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
          {lead.notes}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Programme 20j</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[hsl(var(--accent-navy))] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {(lead.prochaine_action || hot || stale) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {lead.prochaine_action && (
            <span
              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                dueSoon
                  ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                  : "bg-muted text-muted-foreground"
              }`}
              title={`Prochaine action : ${formatDate(lead.prochaine_action)}`}
            >
              <CalendarClock className="h-2.5 w-2.5" />
              {deadlineLabel(lead.prochaine_action) || formatDate(lead.prochaine_action)}
            </span>
          )}
          {hot && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
              <Flame className="h-2.5 w-2.5" />
              Chaud
            </span>
          )}
          {stale && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <Clock className="h-2.5 w-2.5" />
              &gt; 2j
            </span>
          )}
        </div>
      )}
    </div>
  );
}
